import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPath, apiPost, apiPut } from '@/lib/api/client';
import { CubixApiError } from '@/lib/api/types';

// ── Types ─────────────────────────────────────────────────────────────────────

export type MatrixDimension =
  | 'INSTRUMENT_TYPE'   // Instrument type (BOND, T_BILL, LOAN …)
  | 'BS_CLASS'          // Full balance-sheet class (ASSET, LIABILITY, OBS_ASSET, OBS_LIABILITY)
  | 'CURRENCY'          // Deal currency (MAD, EUR, USD …)
  | 'SENS'              // Cashflow direction (INFLOW | OUTFLOW)
  | 'BS_POSITION'       // Binary on/off balance-sheet (BILAN | HORS_BILAN)
  | 'PCEC';             // Moroccan PCEC accounting chapter

export interface MatrixSegmentationMapping {
  id?: string;
  dimensionValue: string;   // raw value, e.g. 'BOND'
  segmentLabel: string;     // display label, e.g. 'Titres souverains'
  sortOrder?: number;
}

export interface MatrixSegmentation {
  id: string;
  name: string;
  description?: string;
  dimension: MatrixDimension;
  isPreset: boolean;
  sortOrder?: number;
  mappings: MatrixSegmentationMapping[];
  createdAt?: string;
  updatedAt?: string;
}

export type MatrixSegmentationCreate = Omit<MatrixSegmentation, 'id' | 'isPreset' | 'createdAt' | 'updatedAt'>;
export type MatrixSegmentationUpdate = MatrixSegmentationCreate & { id: string };

// ── Query keys ────────────────────────────────────────────────────────────────

const KEYS = {
  all:  () => ['segmentations'] as const,
  one:  (id: string) => ['segmentations', id] as const,
};

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useSegmentations(
  options?: Omit<UseQueryOptions<MatrixSegmentation[], CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.all(),
    queryFn:  () => apiGet<MatrixSegmentation[]>(apiPath('alm', 'matrix-segmentations')),
    ...options,
  });
}

export function useSegmentation(
  id: string | undefined,
  options?: Omit<UseQueryOptions<MatrixSegmentation, CubixApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: KEYS.one(id!),
    enabled:  Boolean(id),
    queryFn:  () => apiGet<MatrixSegmentation>(apiPath('alm', 'matrix-segmentations', id!)),
    ...options,
  });
}

export function useCreateSegmentation() {
  const qc = useQueryClient();
  return useMutation<MatrixSegmentation, CubixApiError, MatrixSegmentationCreate>({
    mutationFn: (body) =>
      apiPost<MatrixSegmentation>(apiPath('alm', 'matrix-segmentations'), body),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all() }),
  });
}

export function useUpdateSegmentation() {
  const qc = useQueryClient();
  return useMutation<MatrixSegmentation, CubixApiError, MatrixSegmentationUpdate>({
    mutationFn: ({ id, ...body }) =>
      apiPut<MatrixSegmentation>(apiPath('alm', 'matrix-segmentations', id), body),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.all() });
      qc.invalidateQueries({ queryKey: KEYS.one(vars.id) });
    },
  });
}

export function useDeleteSegmentation() {
  const qc = useQueryClient();
  return useMutation<void, CubixApiError, string>({
    mutationFn: (id) =>
      apiDelete(apiPath('alm', 'matrix-segmentations', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all() }),
  });
}
